/** Signed avatar URLs keyed by user id; users the caller may not see are absent. */
export type FetchProfileImageUrls = (
  userIds: string[],
) => Promise<Record<string, string>>;

/**
 * How long to collect requests before calling the API. Avatars in one list
 * mount in the same render, so a short window turns them into one call.
 */
const BATCH_WINDOW_MS = 10;

type Waiter = {
  resolve: (url: string | null) => void;
  reject: (error: unknown) => void;
};

/**
 * Coalesce avatar URL requests made close together into as few
 * `profile.getImageUrls` calls as the server's batch limit allows. Each caller
 * still gets its own promise, resolving to `null` when there is no image it
 * may see.
 */
export function createProfileImageUrlBatcher({
  fetchUrls,
  maxBatchSize,
}: {
  fetchUrls: FetchProfileImageUrls;
  maxBatchSize: number;
}): (userId: string) => Promise<string | null> {
  let pending = new Map<string, Waiter[]>();
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  async function fetchChunk({
    userIds,
    waiters,
  }: {
    userIds: string[];
    waiters: Map<string, Waiter[]>;
  }): Promise<void> {
    try {
      const urls = await fetchUrls(userIds);
      for (const userId of userIds) {
        for (const waiter of waiters.get(userId) ?? []) {
          waiter.resolve(urls[userId] ?? null);
        }
      }
    } catch (error) {
      for (const userId of userIds) {
        for (const waiter of waiters.get(userId) ?? []) {
          waiter.reject(error);
        }
      }
    }
  }

  async function flush(): Promise<void> {
    const waiters = pending;
    pending = new Map();
    flushTimer = null;
    const userIds = [...waiters.keys()];
    const chunks: Promise<void>[] = [];
    for (let start = 0; start < userIds.length; start += maxBatchSize) {
      chunks.push(
        fetchChunk({
          userIds: userIds.slice(start, start + maxBatchSize),
          waiters,
        }),
      );
    }
    await Promise.all(chunks);
  }

  return (userId) =>
    new Promise((resolve, reject) => {
      const waiters = pending.get(userId) ?? [];
      waiters.push({ resolve, reject });
      pending.set(userId, waiters);
      if (!flushTimer) {
        flushTimer = setTimeout(() => void flush(), BATCH_WINDOW_MS);
      }
    });
}
